import type { HeightmapAsset, PatternLayer, ReliefFinish } from '../types';
import { createLayer } from '../design/defaults';
import { heightmapFromBlob } from './image';

/**
 * Texture generation providers.
 *
 * "Prompt the AI" is an interface, not a vendor. One provider ships with the
 * app and runs entirely offline; anyone can register another that calls a real
 * image model. Keeping this pluggable is what stops an MIT project from
 * hard-wiring someone else's paid API into its critical path.
 */

export interface TextureGenerationRequest {
  prompt: string;
  /** Overrides the seed derived from the prompt, for "try again". */
  seed?: number;
  signal?: AbortSignal;
}

export interface TextureGenerationResult {
  label: string;
  layers: PatternLayer[];
  finish?: Partial<ReliefFinish>;
  /** Relief depth in millimetres that suits the result. */
  suggestedDepth?: number;
  /** Bitmaps the layers reference; merged into the design's asset table. */
  assets?: HeightmapAsset[];
  /** What the provider did, shown to the user so the result is not a black box. */
  notes: string[];
}

export interface TextureProvider {
  id: string;
  name: string;
  description: string;
  /** False when the provider needs configuration before it can be used. */
  isReady(): boolean;
  /** Why it is not ready, shown in the UI. */
  readinessHint?: string;
  generate(request: TextureGenerationRequest): Promise<TextureGenerationResult>;
}

const providers = new Map<string, TextureProvider>();

export function registerProvider(provider: TextureProvider): TextureProvider {
  providers.set(provider.id, provider);
  return provider;
}

export function listProviders(): TextureProvider[] {
  return [...providers.values()];
}

export function getProvider(id: string): TextureProvider | undefined {
  return providers.get(id);
}

/**
 * Configuration for an image-generating HTTP endpoint.
 *
 * No default endpoint ships with the app: the URL and any credentials are
 * supplied by whoever runs it. The request is a plain JSON POST and the
 * response is expected to be an image, which is the lowest common denominator
 * across hosted models, a self-hosted Stable Diffusion, or a proxy of your own.
 */
export interface ImageEndpointConfig {
  endpoint: string;
  /** Sent as `Authorization: Bearer <token>` when present. */
  token?: string;
  /** Extra fields merged into the request body. */
  body?: Record<string, unknown>;
  /** How the generated bitmap is mapped onto the wall. */
  fit?: 'wall' | 'cover' | 'span' | 'panel';
}

const IMAGE_ENDPOINT_STORAGE_KEY = 'organic3d.imageEndpoint';

export function loadImageEndpointConfig(): ImageEndpointConfig | null {
  try {
    const raw = localStorage.getItem(IMAGE_ENDPOINT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImageEndpointConfig;
    return typeof parsed?.endpoint === 'string' && parsed.endpoint ? parsed : null;
  } catch {
    // A blocked or corrupt localStorage must not break the app.
    return null;
  }
}

export function saveImageEndpointConfig(config: ImageEndpointConfig | null): void {
  try {
    if (config) {
      localStorage.setItem(IMAGE_ENDPOINT_STORAGE_KEY, JSON.stringify(config));
    } else {
      localStorage.removeItem(IMAGE_ENDPOINT_STORAGE_KEY);
    }
  } catch {
    // Ignore: running without persistence is degraded, not broken.
  }
}

/**
 * Provider that asks a user-configured endpoint for an image and imports the
 * result as a height map.
 */
export const imageEndpointProvider: TextureProvider = {
  id: 'image-endpoint',
  name: 'Image endpoint',
  description:
    'Sends the prompt to an image-generation endpoint you configure, then imports the returned picture as relief.',
  readinessHint: 'Add an endpoint URL in the settings below to enable this provider.',

  isReady() {
    return loadImageEndpointConfig() !== null;
  },

  async generate(request) {
    const config = loadImageEndpointConfig();
    if (!config) throw new Error('No image endpoint configured.');

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (config.token) headers.Authorization = `Bearer ${config.token}`;

    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt: request.prompt, ...(config.body ?? {}) }),
      signal: request.signal,
    });

    if (!response.ok) {
      throw new Error(`Image endpoint returned ${response.status} ${response.statusText}.`);
    }

    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) {
      throw new Error(`Expected an image from the endpoint, received "${blob.type}".`);
    }

    const asset = await heightmapFromBlob(blob, { name: request.prompt.slice(0, 40) });

    return {
      label: request.prompt.trim().slice(0, 60) || 'Generated image',
      assets: [asset],
      layers: [
        createLayer('heightmap', {
          params: { asset: asset.id, fit: config.fit ?? 'wall', tile: 'mirror' },
        }),
      ],
      notes: [
        'Imported the generated image as a height map.',
        'Bright areas become high relief; use the Levels controls to adjust.',
      ],
    };
  },
};
